// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { SmartSort, SmartSortProduct } from '@iheartjane/dm-sdk';
import { Typography } from '@iheartjane/reefer';
import { useCallback, useRef } from 'react';

import type { AlgoliaProduct } from '@jane/search/types';
import { useGetStore } from '@jane/shared/data-access';
import type { Store } from '@jane/shared/types';

import { useGetCart } from '../../../data-access';
import type { RootLoaderType } from '../../../data-access';
import { useKioskSpacing, useTypedRouteLoaderData } from '../../../hooks';
import type { AddToBagParams } from '../../../types';
import { horizontallyScrollable, trackAddedProductToBag } from '../../../util';
import { Grid } from '../Grid/Grid';
import { ProductCard, productCardMaxWidth } from '../ProductCard/ProductCard';
import { ProductCardSkeleton } from '../ProductCard/ProductCardSkeleton';
import { useProductDetailsPath } from '../ProductCard/useProductDetailsPath';

const NUM_PLACEHOLDER_PRODUCTS = 7;

const Container = styled(Grid)(() => {
  const { defaultSpacing } = useKioskSpacing({
    defaultPadSize: 48,
    defaultSize: 64,
  });

  return {
    ...horizontallyScrollable,
    paddingRight: defaultSpacing,
    paddingTop: '24px',
  };
});

const CartTopperRefContainer = styled.div({
  paddingBottom: 32,
  position: 'relative' as const,
  zIndex: 1,
});

const RenderCartTopperContainer = styled.div({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  marginBottom: 32,
  position: 'relative' as const,
});

const SponsoredLabel = styled.div({
  bottom: -8,
  position: 'absolute' as const,
  transform: 'translateY(100%)',
});

const StyledProductCard = styled(ProductCard)({
  height: '100%',
});

export type CartTopperProps = {
  isLoading: boolean;
  onAddCartTopper: (props: AddToBagParams) => void;
  row: SmartSort<AlgoliaProduct> | undefined;
};

export const CartToppers = ({
  onAddCartTopper,
  isLoading = false,
  row,
}: CartTopperProps) => {
  const nodeRef = useRef(null);
  const { getKioskSpacing, isIpad } = useKioskSpacing();
  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const { data: cart } = useGetCart();
  const productDetailsPath = useProductDetailsPath({ isCartTopper: true });

  const onAddToBag = useCallback(
    (menuProduct: AlgoliaProduct, addProps: AddToBagParams) => {
      onAddCartTopper(addProps);

      trackAddedProductToBag({
        cart,
        dmType: ['bag'],
        productDetails: {
          product: menuProduct,
          productPercentageOff: addProps.discountPercentage,
          productPriceId: addProps.priceId,
          productSavingsAmount: addProps.discountAmount,
          productSpecialId: menuProduct.special_id,
        },
        quickAdd: true,
        source: ['cart toppers'],
        store,
      });
    },
    [cart, onAddCartTopper, store]
  );

  const renderCartTopper = useCallback(
    (topper: SmartSortProduct<AlgoliaProduct>) => (
      <RenderCartTopperContainer key={`cart-topper-${topper.objectId}`}>
        <StyledProductCard
          highlightDeal
          isTopper
          product={topper}
          navigateTo={productDetailsPath}
          onAddToBag={(addProps) => onAddToBag(topper.attributes, addProps)}
        />
        {topper.isSponsored && (
          <SponsoredLabel>
            <Typography variant="mini" color="grays-mid" textAlign="center">
              Sponsored
            </Typography>
          </SponsoredLabel>
        )}
      </RenderCartTopperContainer>
    ),
    [onAddToBag, productDetailsPath]
  );

  if (!row) {
    return null;
  }

  return (
    <CartTopperRefContainer ref={nodeRef}>
      {row.products.length > 0 && (
        <Typography as="h2" variant={isIpad ? 'body-bold' : 'header-bold'}>
          Add to your order
        </Typography>
      )}
      <Container
        gap={getKioskSpacing(48, 40)}
        gridTemplateColumns={`repeat(${Math.max(
          NUM_PLACEHOLDER_PRODUCTS,
          row?.products.length
        )}, minmax(${productCardMaxWidth}px, 28%))`}
      >
        {isLoading &&
          Array(NUM_PLACEHOLDER_PRODUCTS)
            .fill(null)
            .map((_, index) => <ProductCardSkeleton key={index} />)}
        {!isLoading && row.products.map(renderCartTopper)}
      </Container>
    </CartTopperRefContainer>
  );
};
